function resolveApiBase(): string {
	// 1. Explicit env var (set at build time in Coolify/Cloudflare)
	const envUrl = import.meta.env.VITE_API_URL;
	if (envUrl) return envUrl;

	// 2. Auto-detect in production: app.oreincantiere.it → api.oreincantiere.it
	if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
		const apiHost = window.location.hostname.replace(/^app\./, 'api.');
		return `${window.location.protocol}//${apiHost}/api/v1`;
	}

	// 3. Local dev fallback
	return 'http://localhost:3000/api/v1';
}

const API_BASE = resolveApiBase();

// ─── Token management ────────────────────────────────────────
// Tokens are stored in BOTH localStorage and cookies.
// Cookies survive the browser → installed PWA transition on iOS/Safari,
// where localStorage contexts are separate.

function setCookie(name: string, value: string, maxAgeDays: number) {
	const secure = location.protocol === 'https:' ? ';Secure' : '';
	document.cookie = `${name}=${encodeURIComponent(value)};path=/;SameSite=Strict;max-age=${maxAgeDays * 86400}${secure}`;
}

function getCookie(name: string): string | null {
	const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
	return match ? decodeURIComponent(match[1] as string) : null;
}

function deleteCookie(name: string) {
	document.cookie = `${name}=;path=/;max-age=0`;
}

function getToken(): string | null {
	return localStorage.getItem('auth_token') ?? getCookie('auth_token');
}

export function setToken(token: string) {
	localStorage.setItem('auth_token', token);
	setCookie('auth_token', token, 1); // 1 day — matches JWT expiry
}

export function setRefreshToken(token: string) {
	localStorage.setItem('refresh_token', token);
	setCookie('refresh_token', token, 30); // 30 days — matches refresh token expiry
}

function getRefreshToken(): string | null {
	return localStorage.getItem('refresh_token') ?? getCookie('refresh_token');
}

export function clearToken() {
	localStorage.removeItem('auth_token');
	localStorage.removeItem('refresh_token');
	localStorage.removeItem('user_nome');
	localStorage.removeItem('dipendente_id');
	deleteCookie('auth_token');
	deleteCookie('refresh_token');
}

export function isAuthenticated(): boolean {
	return getToken() !== null;
}

export function setUserNome(nome: string) {
	localStorage.setItem('user_nome', nome);
}

export function getUserNome(): string | null {
	return localStorage.getItem('user_nome');
}

export function setDipendenteId(id: string) {
	localStorage.setItem('dipendente_id', id);
}

export function getDipendenteId(): string | null {
	const stored = localStorage.getItem('dipendente_id');
	if (stored) return stored;
	// Fallback: decode JWT payload to extract dipendente_id
	const token = getToken();
	if (!token) return null;
	try {
		const parts = token.split('.');
		if (parts.length < 2) return null;
		const payload = JSON.parse(atob(parts[1] as string));
		if (payload.dipendente_id) {
			localStorage.setItem('dipendente_id', payload.dipendente_id);
			return payload.dipendente_id;
		}
	} catch {
		/* invalid token */
	}
	return null;
}

// ─── Refresh logic (shared promise to avoid concurrent refreshes) ─

/** Read current token identity; the display cache can belong to an earlier login. */
export function getQueueOwnerId(): string | null {
	try {
		const encoded = getToken()?.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/');
		if (!encoded) return null;
		const payload = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=')));
		return typeof payload.dipendente_id === 'string' && typeof payload.azienda_id === 'string'
			? payload.azienda_id + ':' + payload.dipendente_id
			: null;
	} catch {
		return null;
	}
}

let refreshPromise: Promise<boolean> | null = null;

/** Try to refresh the access token using the stored refresh token.
 *  Called automatically on 401, and also at startup when the app opens
 *  in standalone mode with only a refresh token (cookie) available. */
export async function tryRefreshToken(): Promise<boolean> {
	if (!refreshPromise) {
		refreshPromise = tryRefresh().finally(() => {
			refreshPromise = null;
		});
	}
	return refreshPromise;
}

/** Returns true if there's no access token but a refresh token is available (e.g. cookie from browser). */
export function hasRefreshTokenOnly(): boolean {
	return !localStorage.getItem('auth_token') && getRefreshToken() !== null;
}

async function tryRefresh(): Promise<boolean> {
	const rt = getRefreshToken();
	if (!rt) return false;

	try {
		const res = await fetch(`${API_BASE}/auth/pwa-refresh`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ refreshToken: rt }),
		});

		if (!res.ok) {
			if (getRefreshToken() === rt && (res.status === 401 || res.status === 403)) clearToken();
			return false;
		}

		const body = await res.json();
		if (getRefreshToken() !== rt) return false;
		setToken(body.data.accessToken);
		setRefreshToken(body.data.refreshToken);
		return true;
	} catch {
		return false;
	}
}

// ─── HTTP client with auto-refresh ───────────────────────────

async function request<T>(
	path: string,
	opts: RequestInit = {},
	isRetry = false,
	expectedOwner?: string,
): Promise<T> {
	if (expectedOwner && getQueueOwnerId() !== expectedOwner)
		throw new ApiError(401, 'Il dipendente connesso è cambiato. La timbratura resta in attesa.');
	const token = getToken();
	const headers: Record<string, string> = {
		...(opts.body ? { 'Content-Type': 'application/json' } : {}),
		...(opts.headers as Record<string, string>),
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	let res: Response;
	let responseText: string;
	const timeout = new AbortController();
	const timer = setTimeout(() => timeout.abort(), 30_000);
	try {
		res = await fetch(`${API_BASE}${path}`, {
			...opts,
			signal: opts.signal ?? timeout.signal,
			headers,
		});
		responseText = await res.text();
	} catch {
		const offlineMsg = !navigator.onLine
			? 'Sei offline. Riprova quando torni online.'
			: 'Impossibile contattare il server. Verifica la connessione internet.';
		throw new ApiError(
			0,
			offlineMsg + ' Esito non confermato. Le timbrature salvate restano nel riepilogo.',
		);
	} finally {
		clearTimeout(timer);
	}

	// Auto-refresh on 401, retry once
	if (res.status === 401 && !isRetry) {
		const rt = getRefreshToken();
		console.log(`[API] 401 on ${path} | hasRefreshToken=${!!rt} | attempting refresh...`);
		if (rt) {
			if (!refreshPromise) {
				refreshPromise = tryRefresh().finally(() => {
					refreshPromise = null;
				});
			}
			const refreshed = await refreshPromise;
			console.log(`[API] refresh result: ${refreshed}`);
			if (refreshed) {
				return request<T>(path, opts, true, expectedOwner);
			}
		} else {
			console.log('[API] No refresh token — skipping refresh');
		}
		// Refresh failed — fall through to error
	}

	if (!res.ok) {
		let body: { message?: string; error?: string } = {};
		try {
			body = JSON.parse(responseText) ?? {};
		} catch {
			/* non-JSON error */
		}
		const serverMsg = body.message ?? body.error;

		// Session corrupt (e.g. missing azienda_id) — force re-auth
		if (body.error === 'MISSING_AZIENDA') {
			clearToken();
			throw new SessionExpiredError();
		}

		throw new ApiError(res.status, translateError(res.status, serverMsg));
	}

	// 204 No Content — no body to parse
	if (res.status === 204) return undefined as T;

	try {
		return JSON.parse(responseText);
	} catch {
		throw new ApiError(
			0,
			'Risposta del server incompleta: esito non confermato. Riprova dal riepilogo.',
		);
	}
}

function translateError(status: number, serverMsg?: string): string {
	// If the server already sent an Italian message, use it
	if (serverMsg && !serverMsg.startsWith('Failed') && serverMsg !== 'Unauthorized') {
		return serverMsg;
	}

	switch (status) {
		case 401:
			return 'Token non valido o scaduto. Chiedi un nuovo link al tuo responsabile.';
		case 403:
			return 'Accesso non autorizzato.';
		case 404:
			return 'Risorsa non trovata.';
		case 429:
			return 'Troppi tentativi. Riprova tra qualche minuto.';
		case 500:
			return 'Errore del server. Riprova più tardi.';
		default:
			return serverMsg ?? 'Errore sconosciuto. Riprova.';
	}
}

export class ApiError extends Error {
	constructor(
		public status: number,
		message: string,
	) {
		super(message);
		this.name = 'ApiError';
	}
}

/**
 * Thrown when the session is corrupt (e.g. missing azienda_id).
 * The UI should show the auth screen with a re-login message.
 */
export class SessionExpiredError extends Error {
	constructor() {
		super(
			'La tua sessione non è più valida. Riclicca il link ricevuto su WhatsApp o chiedi un nuovo link al tuo responsabile.',
		);
		this.name = 'SessionExpiredError';
	}
}

// ─── Auth ────────────────────────────────────────────────────

export async function pwaAuth(token: string) {
	const res = await request<{
		data: { accessToken: string; refreshToken: string; dipendente_id: string; nome: string };
	}>('/auth/pwa-auth', {
		method: 'POST',
		body: JSON.stringify({ token }),
	});
	console.log('[API] pwaAuth response:', {
		hasAccessToken: !!res.data?.accessToken,
		accessTokenLen: res.data?.accessToken?.length,
		hasRefreshToken: !!res.data?.refreshToken,
		nome: res.data?.nome,
	});
	return res.data;
}

// ─── Timbrature ──────────────────────────────────────────────

interface ActionPayload {
	latitudine?: number;
	longitudine?: number;
	accuratezza_gps_m?: number;
	gps_position_timestamp?: string;
	device_timestamp: string;
	note?: string;
	pausa_minuti?: number;
}

interface CantiereActionPayload extends ActionPayload {
	cantiere_id: string;
	nota_lavoro?: string;
}

interface SessioneProvvisoriaActionPayload extends ActionPayload {
	sessione_id?: string;
	nota_lavoro?: string;
}

export async function entrata(
	payload: ActionPayload,
	idempotencyKey?: string,
	expectedOwner?: string,
) {
	return request<{ data: unknown }>(
		'/timbrature/entrata',
		{
			method: 'POST',
			headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
			body: JSON.stringify(payload),
		},
		false,
		expectedOwner,
	);
}

export async function uscita(
	payload: ActionPayload,
	idempotencyKey?: string,
	expectedOwner?: string,
) {
	return request<{ data: unknown }>(
		'/timbrature/uscita',
		{
			method: 'POST',
			headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
			body: JSON.stringify(payload),
		},
		false,
		expectedOwner,
	);
}

export async function riapriGiornata(
	payload: ActionPayload,
	idempotencyKey?: string,
	expectedOwner?: string,
) {
	return request<{ data: unknown }>(
		'/timbrature/riapri',
		{
			method: 'POST',
			headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
			body: JSON.stringify(payload),
		},
		false,
		expectedOwner,
	);
}

export async function entraCantiere(
	payload: CantiereActionPayload,
	idempotencyKey?: string,
	expectedOwner?: string,
): Promise<{ data: unknown; warning?: string }> {
	return request<{ data: unknown; warning?: string }>(
		'/timbrature/cantiere/entrata',
		{
			method: 'POST',
			headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
			body: JSON.stringify(payload),
		},
		false,
		expectedOwner,
	);
}

export async function esciCantiere(
	payload: CantiereActionPayload,
	idempotencyKey?: string,
	expectedOwner?: string,
) {
	return request<{ data: unknown }>(
		'/timbrature/cantiere/uscita',
		{
			method: 'POST',
			headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
			body: JSON.stringify(payload),
		},
		false,
		expectedOwner,
	);
}

export async function entraSessioneProvvisoria(
	payload: ActionPayload,
	idempotencyKey?: string,
	expectedOwner?: string,
): Promise<{ data: unknown; warning?: string }> {
	return request<{ data: unknown; warning?: string }>(
		'/timbrature/cantiere/entrata-provvisoria',
		{
			method: 'POST',
			headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
			body: JSON.stringify(payload),
		},
		false,
		expectedOwner,
	);
}

export async function esciSessioneProvvisoria(
	payload: SessioneProvvisoriaActionPayload,
	idempotencyKey?: string,
	expectedOwner?: string,
) {
	return request<{ data: unknown }>(
		'/timbrature/cantiere/uscita-provvisoria',
		{
			method: 'POST',
			headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
			body: JSON.stringify(payload),
		},
		false,
		expectedOwner,
	);
}

export async function getSuggerimentiLavoro(cantiereId: string, q?: string) {
	const params = new URLSearchParams({ cantiere_id: cantiereId });
	if (q) params.set('q', q);
	return request<{ data: string[] }>(`/timbrature/suggerimenti-lavoro?${params}`);
}

export async function getGiornata(data: string) {
	return request<{ data: unknown | null }>(`/timbrature/giornata/${data}`);
}

export async function listTimbrature(params?: {
	page?: number;
	limit?: number;
	data_da?: string;
	data_a?: string;
	stato?: string;
}) {
	const qs = new URLSearchParams();
	if (params?.page) qs.set('page', String(params.page));
	if (params?.limit) qs.set('limit', String(params.limit));
	if (params?.data_da) qs.set('data_da', params.data_da);
	if (params?.data_a) qs.set('data_a', params.data_a);
	if (params?.stato) qs.set('stato', params.stato);
	const query = qs.toString();
	return request<{ data: unknown[]; total: number; page: number; limit: number }>(
		`/timbrature${query ? `?${query}` : ''}`,
	);
}

export async function getTimbratureConfig() {
	return request<{
		data: {
			pausa_default_minuti: number;
			gps_controllo_abilitato: boolean;
			gps_server_abilitato: boolean;
			gps_accuratezza_massima_m: number;
			gps_retention_giorni: number;
		};
	}>('/timbrature/config');
}

// ─── Cantieri ────────────────────────────────────────────────

export async function listCantieri() {
	return request<{ data: Array<{ id: string; nome: string; lat?: number; lng?: number }> }>(
		'/cantieri?limit=200&stato=attivo',
	);
}

// ─── Le mie ore ──────────────────────────────────────────────

export interface GiornoOre {
	data: string;
	ore: string;
	stato: string | null;
	auto_chiusa: boolean | null;
	sessioni: number;
}

export interface MieOreResponse {
	data: {
		visibile: boolean;
		giorni: GiornoOre[];
		ore_totali: string;
		giorni_lavorati: number;
		stato_riepilogo: string | null;
		messaggio?: string;
	};
	anno: number;
	mese: number;
}

export async function getMieOre(anno?: number, mese?: number) {
	const qs = new URLSearchParams();
	if (anno) qs.set('anno', String(anno));
	if (mese) qs.set('mese', String(mese));
	const query = qs.toString();
	return request<MieOreResponse>(`/timbrature/mie-ore${query ? `?${query}` : ''}`);
}

// ─── Buste paga ──────────────────────────────────────────────

export interface BustaPagaMese {
	anno: number;
	mese: number;
	stato: string | null;
}

export async function getBustePagaDisponibili() {
	return request<{ data: BustaPagaMese[] }>('/report/buste-paga-disponibili');
}

export async function getBustaPagaUrl(dipendenteId: string, anno: number, mese: number) {
	return request<{ data: { url: string; anno: number; mese: number } }>(
		`/report/busta-paga/${dipendenteId}/${anno}/${mese}`,
	);
}

// ─── Documenti ───────────────────────────────────────────────

export type TipoDocumento = 'rapportino' | 'ddt' | 'spesa' | 'carburante';

export interface DocumentoPublic {
	id: string;
	tipo: TipoDocumento;
	data: string;
	foto_urls: string[];
	ai_validato: boolean;
	stato: string;
	nota_dipendente?: string;
	created_at: string;
}

/**
 * Upload a document photo through the backend (avoids CORS with R2).
 * Single-step: the backend handles R2 upload + record creation + OCR.
 */
export async function uploadDocument(
	file: File,
	tipo: TipoDocumento,
	cantiereId?: string,
	notaDipendente?: string,
	target?: { data?: string; timbratura_id?: string },
): Promise<DocumentoPublic> {
	const token = getToken();
	const formData = new FormData();
	// Fields MUST come before the file — @fastify/multipart only reads fields
	// that appear before the first file part in the multipart stream.
	formData.append('tipo', tipo);
	if (cantiereId) formData.append('cantiere_id', cantiereId);
	if (notaDipendente) formData.append('nota_dipendente', notaDipendente);
	if (target?.data) formData.append('data', target.data);
	if (target?.timbratura_id) formData.append('timbratura_id', target.timbratura_id);
	formData.append('file', file);

	let res: Response;
	try {
		res = await fetch(`${API_BASE}/documenti/upload`, {
			method: 'POST',
			headers: token ? { Authorization: `Bearer ${token}` } : {},
			body: formData,
		});
	} catch {
		const msg = !navigator.onLine
			? 'Sei offline. La foto verrà caricata quando torni online.'
			: 'Impossibile caricare la foto. Verifica la connessione internet e riprova.';
		throw new ApiError(0, msg);
	}

	if (res.status === 401 && getRefreshToken()) {
		if (!refreshPromise) {
			refreshPromise = tryRefresh().finally(() => {
				refreshPromise = null;
			});
		}
		const refreshed = await refreshPromise;
		if (refreshed) {
			return uploadDocument(file, tipo, cantiereId, notaDipendente);
		}
	}

	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		const serverMsg = body.message ?? body.error;
		throw new ApiError(res.status, translateError(res.status, serverMsg));
	}

	const body = await res.json();
	return body.data;
}

/**
 * Step 1: Request a presigned upload URL from the backend.
 * The backend creates a documento record and returns the URL for direct R2 upload.
 */
export async function requestDocumentUpload(payload: {
	tipo: TipoDocumento;
	cantiere_id?: string;
	nota_dipendente?: string;
	content_type: string;
	filename: string;
}) {
	return request<{ data: DocumentoPublic; uploadUrl: string }>('/documenti/request-upload', {
		method: 'POST',
		body: JSON.stringify(payload),
	});
}

/**
 * Step 2: Upload the file directly to R2 using the presigned URL.
 * This bypasses the backend — the file goes straight to Cloudflare R2.
 */
export async function uploadFileToR2(presignedUrl: string, file: Blob, contentType: string) {
	let res: Response;
	try {
		res = await fetch(presignedUrl, {
			method: 'PUT',
			headers: { 'Content-Type': contentType },
			body: file,
		});
	} catch {
		const msg = !navigator.onLine
			? 'Sei offline. La foto verrà caricata quando torni online.'
			: 'Impossibile caricare la foto. Verifica la connessione internet e riprova.';
		throw new ApiError(0, msg);
	}
	if (!res.ok) {
		throw new ApiError(res.status, 'Errore durante il caricamento della foto. Riprova.');
	}
}

/**
 * Step 3: Confirm the upload is complete so the backend generates readable URLs.
 */
export async function confirmDocumentUpload(documentoId: string) {
	return request<{ data: DocumentoPublic }>('/documenti/confirm-upload', {
		method: 'POST',
		body: JSON.stringify({ documento_id: documentoId }),
	});
}

/**
 * Get my documents for today.
 */
export async function getMyDocumentsToday() {
	return request<{ data: DocumentoPublic[] }>('/documenti/miei');
}

/**
 * Get a new presigned upload URL for a document whose upload failed.
 */
export async function retryDocumentUpload(documentoId: string) {
	return request<{ uploadUrl: string }>(`/documenti/${documentoId}/retry-upload`, {
		method: 'POST',
	});
}

/**
 * Delete a failed document (only if stato = 'ricevuto' and no photo uploaded).
 */
export async function deleteDocument(documentoId: string) {
	return request<void>(`/documenti/${documentoId}`, {
		method: 'DELETE',
	});
}

/**
 * Get the tesserino PDF URL for the current dipendente.
 */
export function getTesserinoUrl(dipendenteId: string): string {
	const token = getToken();
	return `${API_BASE}/dipendenti/${dipendenteId}/tesserino?token=${token ?? ''}`;
}

/**
 * Tesserino data for digital badge display.
 */
export interface TesserinoData {
	nome: string;
	cognome: string;
	codice_fiscale: string | null;
	data_nascita: string | null;
	luogo_nascita: string | null;
	mansione: string | null;
	data_assunzione: string | null;
	foto_url: string | null;
	azienda_nome: string;
	azienda_logo_url: string | null;
	formazione: Array<{
		id: string;
		tipo_corso: string;
		data_scadenza: string | null;
		has_allegato: boolean;
	}>;
	idoneita: {
		id: string;
		esito: string;
		data_scadenza: string | null;
		data_visita: string;
		has_allegato: boolean;
	} | null;
	verifica_token: string;
}

export async function getTesserinoData(dipendenteId: string): Promise<TesserinoData> {
	const res = await request<{ data: TesserinoData }>(`/dipendenti/${dipendenteId}/tesserino-data`);
	return res.data;
}

/**
 * Get presigned URL for a formazione attestato.
 */
export async function getFormazioneAllegatoUrl(formazioneId: string): Promise<string> {
	const res = await request<{ data: { url: string } }>(`/formazione/${formazioneId}/allegato`);
	return res.data.url;
}

/**
 * Get presigned URL for an idoneita certificato.
 */
export async function getIdoneitaAllegatoUrl(idoneitaId: string): Promise<string> {
	const res = await request<{ data: { url: string } }>(`/idoneita/${idoneitaId}/allegato`);
	return res.data.url;
}

export function getVerificaUrl(token: string): string {
	// Build the public verification URL
	const base = API_BASE.replace('/api/v1', '');
	return `${base}/api/v1/verifica/${token}`;
}

// ─── Push Notifications ──────────────────────────────────────

export async function getVapidPublicKey() {
	return request<{ data: { vapidPublicKey: string } }>('/auth/vapid-public-key');
}

export async function pushSubscribe(subscription: PushSubscriptionJSON) {
	return request<{ data: { id: string } }>('/auth/push-subscribe', {
		method: 'POST',
		body: JSON.stringify({
			endpoint: subscription.endpoint,
			keys: subscription.keys,
		}),
	});
}

export async function pushUnsubscribe(endpoint: string) {
	return request<{ data: { ok: boolean } }>('/auth/push-unsubscribe', {
		method: 'POST',
		body: JSON.stringify({ endpoint }),
	});
}

/**
 * Re-sync an existing push subscription to the backend.
 * Safe to call at startup — does NOT request permission.
 * If permission is already 'granted' but no PushManager subscription exists
 * (e.g. after PWA reinstall), creates a new subscription silently.
 */
export async function resyncPushSubscription(): Promise<boolean> {
	try {
		if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
		if (Notification.permission !== 'granted') return false;

		const registration = await navigator.serviceWorker.ready;
		let subscription = await registration.pushManager.getSubscription();

		if (!subscription) {
			// Permission granted but no subscription — create one (no user gesture needed)
			console.log('[Push] Permission granted but no subscription — creating...');
			try {
				const { data } = await getVapidPublicKey();
				const vapidKey = urlBase64ToUint8Array(data.vapidPublicKey);
				subscription = await registration.pushManager.subscribe({
					userVisibleOnly: true,
					applicationServerKey: vapidKey as BufferSource,
				});
				console.log('[Push] Subscription created at startup');
			} catch (subErr) {
				console.warn('[Push] Failed to create subscription at startup:', subErr);
				return false;
			}
		}

		await pushSubscribe(subscription.toJSON());
		console.log('[Push] Subscription synced to backend');
		return true;
	} catch (err) {
		console.warn('[Push] Resync failed:', err);
		return false;
	}
}

/**
 * Subscribe to push notifications. Requests permission if needed.
 * ⚠️ MUST be called from a user gesture (click/tap) on iOS.
 * Returns true if subscription succeeded or was already active.
 */
export async function subscribeToPush(): Promise<{ ok: boolean; reason?: string }> {
	try {
		if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
			console.warn('[Push] PushManager not available');
			return { ok: false, reason: 'Il browser non supporta le notifiche push' };
		}
		if (Notification.permission === 'denied') {
			console.warn('[Push] Notifications denied by user');
			return { ok: false, reason: 'denied' };
		}

		const registration = await navigator.serviceWorker.ready;

		// Check if already subscribed
		const existing = await registration.pushManager.getSubscription();
		if (existing) {
			// Re-send to backend (idempotent upsert)
			await pushSubscribe(existing.toJSON());
			console.log('[Push] Existing subscription sent to backend');
			return { ok: true };
		}

		// Need permission first — requires user gesture on iOS
		console.log('[Push] Requesting notification permission...');
		const permission = await Notification.requestPermission();
		if (permission !== 'granted') {
			console.warn('[Push] Permission not granted:', permission);
			return { ok: false, reason: permission === 'denied' ? 'denied' : 'Permesso non concesso' };
		}

		// Get VAPID key from server
		let vapidResponse: { data: { vapidPublicKey: string } };
		try {
			vapidResponse = await getVapidPublicKey();
		} catch (vapidErr) {
			console.error('[Push] Failed to get VAPID key:', vapidErr);
			return { ok: false, reason: 'Notifiche push non configurate sul server' };
		}
		const vapidKey = urlBase64ToUint8Array(vapidResponse.data.vapidPublicKey);

		// Create push subscription
		console.log('[Push] Creating PushManager subscription...');
		const subscription = await registration.pushManager.subscribe({
			userVisibleOnly: true,
			applicationServerKey: vapidKey as BufferSource,
		});

		// Send to backend
		await pushSubscribe(subscription.toJSON());
		console.log('[Push] New subscription saved to backend');
		return { ok: true };
	} catch (err) {
		console.error('[Push] Subscription failed:', err);
		return { ok: false, reason: 'Errore imprevisto. Riprova.' };
	}
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
	const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
	const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
	const rawData = atob(base64);
	const outputArray = new Uint8Array(rawData.length);
	for (let i = 0; i < rawData.length; ++i) {
		outputArray[i] = rawData.charCodeAt(i);
	}
	return outputArray;
}
